import { BuyerUI } from '@choco-admin/types';
import { useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';

import { useBuyersPageGetBuyerDetailsQuery } from '../../../generated';
import { mapToBuyerUI, type GeneratedBuyerFragment } from '../utils';

export type CaptureExceptionFunction = (error: Error) => void;

export type UseGetBuyerResponse = {
  buyer: BuyerUI | null;
  isLoading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
};

export type UseGetBuyerConfig = {
  buyerId?: string;
  captureException?: CaptureExceptionFunction;
};

export const useGetBuyer = (
  config?: UseGetBuyerConfig
): UseGetBuyerResponse => {
  const { buyerId: paramBuyerId } = useParams<{ buyerId: string }>();
  const buyerId = config?.buyerId || paramBuyerId;
  const { captureException } = config || {};

  const {
    data,
    loading,
    error: apolloError,
    refetch,
    // BuyersPageGetBuyerDetails returns the aliased address fields that
    // mapToBuyerUI reads (addressLatitude, addressGooglePlaceId, …) and is
    // the query useUpdateBuyer refetches. The legacy GetBuyerDetailsQuery
    // returned raw field names, so google place data was silently dropped
    // (breaking re-saves of google-sourced addresses) and post-save
    // refetches never refreshed this page.
  } = useBuyersPageGetBuyerDetailsQuery({
    variables: { buyerId: buyerId || '' },
    skip: !buyerId,
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    notifyOnNetworkStatusChange: true,
    errorPolicy: 'all',
  });

  const buyer = useMemo(() => {
    if (!data?.buyer) return null;

    try {
      return mapToBuyerUI(data.buyer as GeneratedBuyerFragment);
    } catch (error) {
      captureException?.(error as Error);
      return null;
    }
  }, [data?.buyer, captureException]);

  const handleRefetch = useCallback(async () => {
    try {
      await refetch();
    } catch (error) {
      captureException?.(error as Error);
      throw error;
    }
  }, [refetch, captureException]);

  return useMemo(
    () => ({
      buyer,
      isLoading: loading,
      error: apolloError || null,
      refetch: handleRefetch,
    }),
    [buyer, loading, apolloError, handleRefetch]
  );
};

// Legacy export for backward compatibility
export const useBuyerDetails = () => {
  const result = useGetBuyer();
  return {
    loading: result.isLoading,
    error: result.error,
    buyer: result.buyer,
    refetch: result.refetch,
  };
};
